import { OverlayService } from 'app/services/overlay.service';
import { BbColorPanelComponent } from './../bb-color-panel/bb-color-panel.component';
import { OverlayRef, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { Component, Input, forwardRef, OnInit, OnDestroy, HostBinding, Optional, Self, ViewChild, ElementRef, ViewContainerRef } from '@angular/core';
import { ControlValueAccessor, UntypedFormControl, NgControl } from '@angular/forms';
import { MatFormFieldControl } from '@angular/material/form-field';
import { Subject, ReplaySubject, Subscription, debounceTime } from 'rxjs';

@Component({
	selector: 'bb-color-picker-box',
	templateUrl: './bb-color-picker-box.component.html',
	styleUrls: ['./bb-color-picker-box.component.scss'],
	providers: [{
		provide: MatFormFieldControl,
		useExisting: forwardRef(() => BbColorPickerBoxComponent)
	}]
})

export class BbColorPickerBoxComponent implements MatFormFieldControl<any>, ControlValueAccessor, OnInit, OnDestroy {
	// for MatFormFieldControl
	static nextId = 0;
	@HostBinding() id = `bb-color-picker-box-${BbColorPickerBoxComponent.nextId++}`;
	@HostBinding('class.floating')
	get shouldLabelFloat() {
		return this.focused || !this.empty;
	}
	@HostBinding('class.dark-theme')
	get isDarkTheme() {
		return this.theme === 'dark';
	}
	@Input('aria-describedby') userAriaDescribedBy: string;

	@ViewChild('inputRef') pickerInput: CdkOverlayOrigin; // only used when useOverlay is true
	@ViewChild('colorPicker') colorPickerElement: any;

	@Input() colorPresets: any[] = [];
	@Input() excludeHexHash = false;
	@Input() hideLabel = false;
	@Input() pickerPosition = 'bottom';
	@Input() default: string;
	@Input() inMatFormField = false;
	@Input() disableClear = false;
	@Input() theme: 'default' | 'dark' = 'default';
	@Input() useOverlay = false;
	closeColorPickerSubject = new ReplaySubject<string>(1);
	changeColorSubject = new ReplaySubject<string>(1);
	inputChangedColorSubject = new ReplaySubject<string>(1);
	backdropClickSubject = new Subject<void>();

	colorPickerToggle: boolean;
	modelValue: any;
	defaultIncludingHash: string;

	formControl: UntypedFormControl;
	stateChanges = new Subject<void>();

	// for MatFormFieldControl
	errorState: boolean;
	disabled: boolean;
	required: boolean;
	placeholder: string;
	focused: boolean;
	overlayRef: OverlayRef;

	private subscriptions = new Subscription();

	constructor(@Optional() @Self() public ngControl: NgControl,
		public viewContainerRef: ViewContainerRef,
		private overlayService: OverlayService) {
		this.formControl = new UntypedFormControl();
		// supporting NG_VALUE_ACCESSOR (when not using MatFormFieldControl)
		if (this.ngControl != null) {
			// Setting the value accessor directly (instead of using
			// the providers) to avoid running into a circular import.
			this.ngControl.valueAccessor = this;
		}
	}

	ngOnInit() {
		this.subscriptions.add(this.changeColorSubject.pipe(debounceTime(300)).subscribe((color) => {
			this.onModelChange(color);
		}));

		if (!this.default) {
			return;
		}

		if (this.excludeHexHash) {
			this.defaultIncludingHash = '#' + this.default;
		} else {
			this.defaultIncludingHash = this.default;
		}

	}

	ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
		// for MatFormFieldControl
		this.stateChanges.complete();
	}

	openColorOverlay() {
		if (this.overlayRef) {
			return;
		}

		this.inputChangedColorSubject.next(this.modelValue);

		this.overlayRef = this.overlayService.createConnectedOverlayComponent(
			BbColorPanelComponent,
			this.pickerInput.elementRef,
			this.viewContainerRef,
			{
				inputChangedColorSubject: this.inputChangedColorSubject,
				closeColorPickerSubject: this.closeColorPickerSubject,
				changeColorSubject: this.changeColorSubject,
				backdropClickSubject: this.backdropClickSubject
			},
			[
				{
					function: this.backdropClickSubject.next,
					this: this.backdropClickSubject
				},
				{
					function: () => {
						this.overlayRef = null;
					},
					this: this
				}
			], [{
				originX: 'end',
				originY: 'bottom',
				overlayX: 'end',
				overlayY: 'top',
				offsetX: -10,
				offsetY: -90
			}]
		);
	}

	onTrashClick() {
		this.modelValue = this.defaultIncludingHash ? this.defaultIncludingHash : null;
		this.propagateChange(this.default ? this.default : null);
	}

	onModelChange(val: any) {
		this.modelValue = val;
		if (this.excludeHexHash) {
			val = val.replace('#', '');
		}
		this.propagateChange(val);
	}

	// ControlValueAccessor fields:
	writeValue(value: any) {
		if (this.excludeHexHash && value) {
			value = '#' + value;
		}

		if (!value && this.default) {
			value = this.defaultIncludingHash;
		}

		this.stateChanges.next();
		this.modelValue = value;
	}

	propagateChange = (_: any) => { };

	registerOnChange(fn: any) {
		this.propagateChange = fn;
	}

	registerOnTouched() { }

	// for MatFormFieldControl
	get value() {
		return this.formControl.value;
	}
	get empty() {
		return !this.modelValue;
	}

	setDescribedByIds(ids: string[]) {
		// maybe later, required method.
	}

	onContainerClick(event: MouseEvent) {
		if ((event.target as Element).className === 'inner' || (event.target as Element).className === 'color-preview') {
			return;
		}
		this.colorPickerElement.nativeElement.click();
	}
}
